'use client'

import React from 'react'
import type { Stats, Increase, TermsData, StatKey } from '@/types/statistics'
import type { Locale } from '@/i18n'

interface StatisticsDisplayProps {
  stats: Stats
  increase: Increase
  terms: TermsData
  locale: Locale
  colorScheme?: 'default' | 'blue' | 'slate' | 'primary' | 'card' | 'contrast'
}

export function StatisticsDisplay({ stats, increase, terms, locale }: StatisticsDisplayProps) {
  const formatNumber = (num: number) => {
    // Use comma as thousands separator
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  // Custom term overrides for shorter labels
  const customTerms = {
    uk: {
      atgm_srbm_systems: 'Ракетні комплекси',
      vehicles_fuel_tanks: 'Автотехніка',
      mlrs: 'РСЗВ',
      aa_warfare_systems: 'Системи ПВО',
    },
    en: {
      atgm_srbm_systems: 'Missile systems',
      vehicles_fuel_tanks: 'Vehicles',
      mlrs: 'MLRS',
      aa_warfare_systems: 'Air defense systems',
    },
  }[locale] as Partial<Record<StatKey, string>>

  // Statistics grouped by category
  const categories = [
    {
      titleUk: 'Особовий склад',
      titleEn: 'Personnel',
      items: ['personnel_units'] as StatKey[],
    },
    {
      titleUk: 'Наземна техніка',
      titleEn: 'Ground Forces',
      items: [
        'tanks',
        'armoured_fighting_vehicles',
        'artillery_systems',
        'mlrs',
        'vehicles_fuel_tanks',
        'special_military_equip',
        'atgm_srbm_systems',
      ] as StatKey[],
    },
    {
      titleUk: 'Повітряна техніка',
      titleEn: 'Air Forces',
      items: [
        'planes',
        'helicopters',
        'uav_systems',
        'aa_warfare_systems',
        'cruise_missiles',
      ] as StatKey[],
    },
    {
      titleUk: 'Морська техніка',
      titleEn: 'Naval Forces',
      items: ['warships_cutters', 'submarines'] as StatKey[],
    },
  ]

  return (
    <div className="space-y-4">
      {categories.map((category, categoryIndex) => {
        const categoryTitle = locale === 'uk' ? category.titleUk : category.titleEn

        return (
          <div key={categoryIndex} className={`rounded-lg shadow-md border overflow-hidden`}>
            {/* Category Header */}
            <div className={` px-4 py-2 border-b bg-zinc-800`}>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{categoryTitle}</h3>
            </div>

            {/* Category Items */}
            <ul className={`divide-y bg-zinc-900`}>
              {category.items.map((key) => {
                const term = terms[key]
                const value = stats[key]
                const increaseValue = increase[key]

                if (!term) return null

                // Use custom terms if available, otherwise use API terms
                const displayTitle = customTerms[key] || term.title

                return (
                  <li key={key} className={`px-4 py-2 transition-colors`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-base text-gray-100 dark:text-gray-100">
                        {displayTitle}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-gray-900 dark:text-white text-right">
                          {formatNumber(value)}
                        </span>
                        <span className="font-semibold text-xs font-medium text-red-600 dark:text-red-600 min-w-[38px] mb-2.5 leading-4">
                          {increaseValue > 0 ? `+${formatNumber(increaseValue)}` : ''}
                        </span>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
